import React, { useState, useEffect } from 'react';

const SecurityShieldLoader = ({ isLoading, progress = 0, message = "Processing..." }) => {
  const [animationFrame, setAnimationFrame] = useState(0);

  useEffect(() => {
    if (!isLoading) return;

    const interval = setInterval(() => {
      setAnimationFrame(prev => (prev + 1) % 120); // 120 frame animation cycle
    }, 50);

    return () => clearInterval(interval);
  }, [isLoading]);

  if (!isLoading) return null;

  // Calculate fill percentage for progress ring
  const fillPercentage = Math.max(5, Math.min(95, progress));
  const circumference = 2 * Math.PI * 45; // radius = 45
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (fillPercentage / 100) * circumference;
  
  // Rotating animation for outer ring
  const rotation = (animationFrame * 3) % 360;
  
  // Pulsing animation for shield
  const pulseScale = 1 + Math.sin(animationFrame * 0.1) * 0.05;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
        <div className="text-center">
          {/* Professional Security Shield Loading Animation */}
          <div className="relative mb-6 flex justify-center">
            <svg width="140" height="140" viewBox="0 0 140 140" className="drop-shadow-lg">
              <defs>
                {/* Gradient definitions */}
                <linearGradient id="shieldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#1e40af" />
                  <stop offset="50%" stopColor="#3b82f6" />
                  <stop offset="100%" stopColor="#1d4ed8" />
                </linearGradient>
                
                <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#10b981" />
                  <stop offset="50%" stopColor="#059669" />
                  <stop offset="100%" stopColor="#047857" />
                </linearGradient>
                
                <linearGradient id="lockGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#fbbf24" />
                  <stop offset="100%" stopColor="#f59e0b" />
                </linearGradient>
                
                {/* Filter for glow effect */}
                <filter id="glow">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              
              {/* Outer rotating ring */}
              <circle 
                cx="70" 
                cy="70" 
                r="55" 
                fill="none" 
                stroke="#e5e7eb" 
                strokeWidth="2" 
                opacity="0.3"
                transform={`rotate(${rotation} 70 70)`}
                strokeDasharray="10 5"
              />
              
              {/* Progress ring */}
              <circle
                cx="70"
                cy="70"
                r="45"
                fill="none"
                stroke="#e5e7eb"
                strokeWidth="6"
                opacity="0.3"
              />
              <circle
                cx="70"
                cy="70"
                r="45"
                fill="none"
                stroke="url(#progressGradient)"
                strokeWidth="6"
                strokeLinecap="round"
                strokeDasharray={strokeDasharray}
                strokeDashoffset={strokeDashoffset}
                transform="rotate(-90 70 70)"
                className="transition-all duration-300"
                filter="url(#glow)"
              />
              
              {/* DECLASS Logo */}
              <foreignObject 
                x="20" 
                y="20" 
                width="100" 
                height="100"
                transform={`scale(${pulseScale} ${pulseScale})`}
                style={{ transformOrigin: '70px 70px' }}
              >
                <img 
                  src="/DECLASS GIF.gif" 
                  alt="DECLASS Logo" 
                  className="w-full h-full object-contain drop-shadow-lg"
                  style={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))' }}
                />
              </foreignObject>
              
            </svg>
          </div>
          
          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Classification Progress</span>
              <span className="text-sm font-medium text-indigo-600">{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div 
                className="bg-gradient-to-r from-indigo-500 to-purple-600 h-3 rounded-full transition-all duration-500 ease-out relative"
                style={{ width: `${progress}%` }}
              >
                {/* Animated shine effect */}
                <div className="absolute top-0 left-0 right-0 bottom-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-pulse"></div>
              </div>
            </div>
          </div>
          
          {/* Status Message */}
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              DECLASS Processing
            </h3>
            <p className="text-sm text-gray-600 mb-4">{message}</p>
            
            {/* Rotating classification levels */}
            <div className="flex justify-center space-x-2 text-xs">
              {['UNCLASSIFIED', 'CONFIDENTIAL', 'SECRET', 'TOP SECRET'].map((level, index) => (
                <span 
                  key={level}
                  className={`px-2 py-1 rounded transition-all duration-300 ${
                    (animationFrame + index * 15) % 60 < 15 
                      ? 'bg-indigo-100 text-indigo-800 font-semibold' 
                      : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {level}
                </span>
              ))}
            </div>
          </div>
          
          {/* Pulsing dots */}
          <div className="flex justify-center mt-4 space-x-1">
            {[0, 1, 2].map((dot) => (
              <div
                key={dot}
                className={`w-2 h-2 bg-indigo-500 rounded-full transition-opacity duration-300 ${
                  (animationFrame + dot * 10) % 30 < 15 ? 'opacity-100' : 'opacity-30'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityShieldLoader;